import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export interface TankAdministrationOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface TankAdministrationOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

export function buildTankAdminUpsertOperation(
  context: TankAdministrationOperationContext,
  input: {
    tankId: string;
    expectedVersion: number;
    tankCode: string;
    notes?: string;
    isActive: boolean;
  },
  options?: TankAdministrationOperationOptions,
): OfflineOperation {
  const compact = input.tankCode.trim().toUpperCase().replace(/\s+/g, "");
  const match = /^([BGPQ])(\d{1,3})$/.exec(compact);
  const number = match ? Number(match[2]) : 0;

  if (
    !context.actorId ||
    !context.farmId ||
    !context.deviceId ||
    !context.performedAt ||
    !input.tankId ||
    !match ||
    number < 1 ||
    number > 999
  ) {
    throw new Error("TANK_ADMIN_INPUT_INVALID");
  }

  if (!Number.isInteger(input.expectedVersion) || input.expectedVersion < 0) {
    throw new Error("TANK_ADMIN_VERSION_INVALID");
  }

  const tankCode = `${match[1]}${String(number).padStart(3, "0")}`;
  const notes = input.notes?.trim();

  if (notes && notes.length > 1000) {
    throw new Error("TANK_NOTES_TOO_LONG");
  }

  return createOfflineOperation(
    {
      operationType: "tank.admin.upsert",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.tankId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        tankId: input.tankId,
        expectedVersion: input.expectedVersion,
        tankCode,
        ...(notes ? { notes } : {}),
        isActive: input.isActive,
      },
    },
    {
      ...(options?.clock ? { clock: options.clock } : {}),
      ...(options?.operationIdSource ? { idSource: options.operationIdSource } : {}),
    },
  );
}
